import {
  BRAND_NAME,
  BRAND_TAGLINE,
} from "@/lib/brand";
import { FACETED_A_PATHS, FACETED_A_VIEWBOX } from "@/components/brand/faceted-a-paths";

type FacetedAVariant = "color" | "seal" | "inline";

const [VB_X, VB_Y, VB_W, VB_H] = FACETED_A_VIEWBOX.split(" ").map(Number);

function seamFill(seal: boolean) {
  return seal ? "#0F766E" : "var(--color-background-body, #ffffff)";
}

/** Заглавная «А» — SVG из референса, швы = цвет фона (прозрачные). */
export function FacetedCapitalA({
  height = 42,
  variant = "color",
}: {
  height?: number;
  variant?: FacetedAVariant;
}) {
  const width = height * (VB_W / VB_H);
  const seal = variant === "seal";
  const inline = variant === "inline";
  const gap = seamFill(seal);

  const facets = FACETED_A_PATHS.filter((p) => p.role === "facet");
  const seams = FACETED_A_PATHS.filter((p) => p.role === "seam");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={FACETED_A_VIEWBOX}
      width={width}
      height={height}
      preserveAspectRatio="xMidYMid meet"
      shapeRendering="geometricPrecision"
      role="img"
      aria-hidden={!seal}
      aria-label={seal ? BRAND_NAME : undefined}
      style={{
        display: "inline-block",
        flexShrink: 0,
        verticalAlign: inline ? "baseline" : undefined,
        marginInline: inline ? "0.03em" : undefined,
        overflow: "visible",
      }}
    >
      {seal ? (
        <rect
          x={VB_X}
          y={VB_Y}
          width={VB_W}
          height={VB_H}
          rx={VB_W * 0.1}
          fill="#0F766E"
        />
      ) : null}

      {facets.map((path, i) => (
        <path key={`f-${i}`} fill={path.fill} d={path.d} />
      ))}

      {seams.map((path, i) => (
        <path key={`s-${i}`} fill={gap} d={path.d} />
      ))}
    </svg>
  );
}

function WordmarkColumn({
  fontSize,
  inlineH,
  showTagline,
  smallTagline = false,
}: {
  fontSize: number;
  inlineH: number;
  showTagline: boolean;
  smallTagline?: boolean;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: showTagline ? (smallTagline ? 2 : 4) : 0,
        minWidth: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          fontWeight: 700,
          fontSize,
          lineHeight: 1,
          letterSpacing: "-0.015em",
          whiteSpace: "nowrap",
        }}
      >
        Иде
        <FacetedCapitalA height={inlineH} variant="inline" />
        литика
      </span>
      {showTagline ? (
        <span
          aria-hidden
          className="logo-n-tagline text-[color:var(--color-text-secondary,#6b7280)]"
          style={{
            fontSize: smallTagline ? 7.5 : 9.5,
            fontWeight: 500,
            letterSpacing: smallTagline ? "0.12em" : "0.16em",
            textTransform: "uppercase",
            alignSelf: smallTagline ? "flex-end" : undefined,
          }}
        >
          {BRAND_TAGLINE}
        </span>
      ) : null}
    </span>
  );
}

export function LogoNWordmark({
  compact = false,
  showTagline = true,
  iconOnly = false,
  iconSize,
  /** Слово + tagline без большой «А» слева (как в сайдбаре ЛК). */
  wordmarkOnly = false,
}: {
  compact?: boolean;
  showTagline?: boolean;
  iconOnly?: boolean;
  iconSize?: number;
  wordmarkOnly?: boolean;
}) {
  const fontSize = compact ? 17 : 20;
  const iconH = iconSize ?? (compact ? 34 : 40);
  const inlineH = Math.round(fontSize * 0.7);

  if (iconOnly) {
    return <FacetedCapitalA height={iconH} variant="seal" />;
  }

  const column = (
    <WordmarkColumn
      fontSize={fontSize}
      inlineH={inlineH}
      showTagline={showTagline}
      smallTagline={wordmarkOnly}
    />
  );

  if (wordmarkOnly) {
    return (
      <span
        style={{
          display: "inline-flex",
          lineHeight: 1,
          color: "inherit",
          minWidth: 0,
        }}
      >
        {column}
      </span>
    );
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 9 : 11,
        lineHeight: 1,
        color: "inherit",
        minWidth: 0,
      }}
    >
      <FacetedCapitalA height={iconH} variant="color" />
      {column}
    </span>
  );
}
